import { Dialog } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCirclePlus, TbWallet } from "react-icons/tb";
import { CurrencyIcon } from "../../../Components/Atoms/CurrencyIcon";
import { ModalCloseButton } from "../../../Components/Molecules/ValutDialog";
import { useToast } from "../../../Hooks/toast";
import {
  createStablesWallet,
  getStablesApiErrorMessage,
  getStablesAssetNetworks,
} from "../../../Services/stablesApi";
import {
  DEFAULT_STABLES_CREATE_NETWORK,
  STABLES_CREATE_ASSETS,
  getStablesCreateNetworksForAsset,
  pickDefaultStablesCreateAsset,
  pickDefaultStablesCreateNetwork,
  resolveStablesCreateNetworks,
} from "./constants";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

const chipClass = (selected) =>
  [
    "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
    selected
      ? "border-[#e7a73f] bg-[#e7a73f]/15 text-[#b87a1f] dark:text-[#e7a73f]"
      : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300",
  ].join(" ");

/**
 * Modal to create a stables wallet (first or an additional asset) on the active IB account.
 * Calls MS `POST /v1/stables/wallets` → Baas provision.
 * Rede chips are filtered by selected asset (API catalog, fallback matrix).
 */
export const StablesCreateWalletModal = ({
  open,
  onClose,
  onCreated,
  /** When true, user must create (or leave the page) — hide dismiss. */
  required = false,
  /** Remaining catalog assets (uppercase). Defaults to the full create catalog. */
  assets,
  initialAsset,
}) => {
  const { addToast } = useToast();
  const availableAssets = useMemo(() => {
    if (!Array.isArray(assets)) return [...STABLES_CREATE_ASSETS];
    return assets
      .map((code) => String(code || "").trim().toUpperCase())
      .filter((code) => STABLES_CREATE_ASSETS.includes(code));
  }, [assets]);
  const isAddMore = availableAssets.length > 0
    && availableAssets.length < STABLES_CREATE_ASSETS.length;

  const [asset, setAsset] = useState(() =>
    pickDefaultStablesCreateAsset(availableAssets, initialAsset)
  );
  const [network, setNetwork] = useState(DEFAULT_STABLES_CREATE_NETWORK);
  const [networks, setNetworks] = useState(() =>
    getStablesCreateNetworksForAsset(
      pickDefaultStablesCreateAsset(availableAssets, initialAsset)
    )
  );
  const [loading, setLoading] = useState(false);
  const [networksLoading, setNetworksLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setNetworksLoading(false);
      return;
    }
    setAsset(pickDefaultStablesCreateAsset(availableAssets, initialAsset));
  }, [open, availableAssets, initialAsset]);

  useEffect(() => {
    if (!open || !asset) {
      if (open && !asset) {
        setNetworks([]);
        setNetwork("");
        setNetworksLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    const fallback = getStablesCreateNetworksForAsset(asset);

    setNetworksLoading(true);
    setNetworks(fallback);
    setNetwork((current) => pickDefaultStablesCreateNetwork(fallback, current));

    (async () => {
      try {
        const apiNetworks = await getStablesAssetNetworks(asset);
        if (cancelled) return;
        const next = resolveStablesCreateNetworks(asset, apiNetworks);
        setNetworks(next);
        setNetwork((current) => pickDefaultStablesCreateNetwork(next, current));
      } catch {
        if (cancelled) return;
        setNetworks(fallback);
        setNetwork((current) => pickDefaultStablesCreateNetwork(fallback, current));
      } finally {
        if (!cancelled) setNetworksLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, asset]);

  const canSubmit =
    Boolean(asset) && Boolean(network) && networks.length > 0 && !loading && !networksLoading;

  const handleClose = () => {
    if (loading || required) return;
    onClose?.();
  };

  const handleSelectAsset = (code) => {
    if (loading || code === asset) return;
    setAsset(code);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      const wallet = await createStablesWallet({ asset, network });
      addToast({
        type: "success",
        title: intl.get("stables.noWallet.createSuccess"),
      });
      onCreated?.(wallet);
      onClose?.();
    } catch (error) {
      addToast({
        type: "error",
        title: getStablesApiErrorMessage(
          error,
          intl.get("stables.noWallet.createError"),
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
      disableEscapeKeyDown={required || loading}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <div className="relative border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          {!required ? (
            <ModalCloseButton
              onClick={handleClose}
              className="absolute right-3 top-3"
              disabled={loading}
            />
          ) : null}
          <div className="flex items-center gap-2 pr-8">
            <TbWallet className="h-5 w-5 shrink-0 text-[#e7a73f]" aria-hidden />
            <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
              {intl.get(isAddMore ? "stables.noWallet.addTitle" : "stables.noWallet.title")}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get(isAddMore ? "stables.noWallet.addSubtitle" : "stables.noWallet.subtitle")}
          </p>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("stables.noWallet.assetLabel")}
            </p>
            {availableAssets.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("stables.noWallet.allAssetsHaveWallets")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableAssets.map((code) => {
                  const selected = asset === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      className={chipClass(selected)}
                      aria-pressed={selected}
                      onClick={() => handleSelectAsset(code)}
                      disabled={loading}
                    >
                      <CurrencyIcon asset={code} size={16} />
                      {code}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {availableAssets.length > 0 ? (
            <div>
              <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("stables.noWallet.networkLabel")}
              </p>
              <div className="flex flex-wrap gap-2">
                {networks.map((item) => {
                  const selected = network === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={chipClass(selected)}
                      aria-pressed={selected}
                      onClick={() => setNetwork(item.id)}
                      disabled={loading || networksLoading}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          {!required ? (
            <button
              type="button"
              className="btn-outline w-auto"
              onClick={handleClose}
              disabled={loading}
            >
              {intl.get("common.cancel")}
            </button>
          ) : null}
          <button type="submit" className="btn-primary w-auto" disabled={!canSubmit}>
            <span className="inline-flex items-center gap-1.5">
              <TbCirclePlus className="h-4 w-4" aria-hidden />
              {loading
                ? intl.get("stables.noWallet.creating")
                : asset
                  ? intl.get("stables.noWallet.addCta", { asset })
                  : intl.get("stables.noWallet.createCta")}
            </span>
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default StablesCreateWalletModal;
